import { z } from 'zod';
import { TransparencySettingSchema } from '../setting';
import { ShadowSettingKeySchema } from '../setting/shadow-setting';
import { BasicAnimationSetSchema } from './animations';
import { BasePinComponentSchema, BasePinComponentSettingsSchema } from './base-pin-component';
import { HotspotIconSchema } from './hotspot-pin-component';
import { ShapeTypeSchema } from './shape.utils';

export const ShapePinComponentPinThemeSettingsSchema = z.object({
  fillColor: z.string().min(1).optional(),
  fillOpacity: TransparencySettingSchema,
  borderSize: z.number().optional(),
  borderColor: z.string().optional(),
  shadow: ShadowSettingKeySchema,
  transparency: TransparencySettingSchema,
});

const BaseShapeSettingsSchema = BasePinComponentSettingsSchema.extend(
  ShapePinComponentPinThemeSettingsSchema.extend(
    z.object({
      lineCap: z.enum(['butt', 'round', 'square']).optional(),
      lineJoin: z.enum(['bevel', 'round', 'miter']).optional(),
      strokeDashArray: z.string().optional(),
      strokeOpacity: TransparencySettingSchema,
      animations: BasicAnimationSetSchema.optional(),
    }).shape
  ).shape
);

const RectangleSettingsSchema = BaseShapeSettingsSchema.extend(
  z.object({
    shapeType: z.literal(ShapeTypeSchema.enum.RECTANGLE),
    borderRadius: z.number().optional(),
  }).shape
);
export type RectangleShapeSettings = z.infer<typeof RectangleSettingsSchema>;

const EllipseSettingsSchema = BaseShapeSettingsSchema.extend(
  z.object({
    shapeType: z.literal(ShapeTypeSchema.enum.ELLIPSE),
  }).shape
);
export type EllipseShapeSettings = z.infer<typeof EllipseSettingsSchema>;

const TriangleSettingsSchema = BaseShapeSettingsSchema.extend(
  z.object({
    shapeType: z.literal(ShapeTypeSchema.enum.TRIANGLE),
  }).shape
);
export type TriangleShapeSettings = z.infer<typeof TriangleSettingsSchema>;

const PolygonSettingsSchema = BaseShapeSettingsSchema.extend(
  z.object({
    shapeType: z.literal(ShapeTypeSchema.enum.POLYGON),
    points: z.number().optional(),
  }).shape
);
export type PolygonShapeSettings = z.infer<typeof PolygonSettingsSchema>;

// The star shape is a polygon with extra settings for inner radius
const StarSettingsSchema = PolygonSettingsSchema.extend(
  z.object({
    shapeType: z.literal(ShapeTypeSchema.enum.STAR),
    innerRadius: z.number().optional(),
  }).shape
);
export type StarShapeSettings = z.infer<typeof StarSettingsSchema>;

const ArrowSettingsSchema = BaseShapeSettingsSchema.extend(
  z.object({
    shapeType: z.literal(ShapeTypeSchema.enum.ARROW),
    headAngle: z.number().optional(),
    bodyThickness: z.number().optional(),
  }).shape
);
export type ArrowShapeSettings = z.infer<typeof ArrowSettingsSchema>;

const IconSettingsSchema = BaseShapeSettingsSchema.extend(
  // ToDo: the icon shape doesn't need border styling settings, but omitting these is causing type issues on the frontend.
  // We should fix this in the future, but for now we can just keep these settings
  z.object({
    shapeType: z.literal(ShapeTypeSchema.enum.ICON),
    // ToDo: we can reuse the HotspotIconSchema here for now and make switching between icons feature flagged.
    // We still need to decide with Design which set of icons we want to support and how this overlaps with the Hotspot icons.
    // It should be fine to change this icon schema, as long as we keep the ROCKET icon and keep the switching of icons feature flagged.
    iconName: HotspotIconSchema.optional(),
  }).shape
);
export type IconShapeSettings = z.infer<typeof IconSettingsSchema>;

const ShapeSettingsSchema = z.discriminatedUnion('shapeType', [
  RectangleSettingsSchema,
  EllipseSettingsSchema,
  TriangleSettingsSchema,
  PolygonSettingsSchema,
  StarSettingsSchema,
  ArrowSettingsSchema,
  IconSettingsSchema,
]);
export type ShapeSettings = z.infer<typeof ShapeSettingsSchema>;

export const ShapePinComponentTypeSchema = z.literal('SHAPE');

export const ShapePinComponentSchema = BasePinComponentSchema.extend(
  z.object({
    type: ShapePinComponentTypeSchema,
    settings: ShapeSettingsSchema,
  }).shape
);
